import React, { useState, useEffect } from "react";
import "./TrainingManDaysCalculation.css";
import "./EmployeeTrainingIdentified.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { getDistinctFilters } from '../Components/Menu/Branch';
import BatchViewer from '../Components/Viewers/BatchViewer';
import FacultyViewer from '../Components/Viewers/FacultyViewer';
import { batchStatusAPI, facultyAPI } from "./api";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

function TrainingManDaysCalculation() {
  const [batchType, setBatchType] = useState("");
  const [branchName, setBranchName] = useState("");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [fromDateOpen, setFromDateOpen] = useState(false);
  const [toDateOpen, setToDateOpen] = useState(false);
  const [batchSrNo, setBatchSrNo] = useState("");
  const [faculty, setFaculty] = useState("");
  const [facultyCode, setFacultyCode] = useState("");
  const [showBatchViewer, setShowBatchViewer] = useState(false);
  const [showFacultyViewer, setShowFacultyViewer] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [showTable, setShowTable] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [branches, setBranches] = useState([]);
  const [facultyMap, setFacultyMap] = useState({});

  useEffect(() => {
    getDistinctFilters().then(({ branches: b }) => setBranches(b));
    facultyAPI.getAll().then((res) => {
      const map = {};
      (Array.isArray(res) ? res : []).forEach(f => { map[String(f.faculty_code)] = f.faculty_name; });
      setFacultyMap(map);
    }).catch(() => {});
  }, []);

  const handleBatchSelect = (batch) => {
    setBatchSrNo(batch.batch_sr_no || "");
    setShowBatchViewer(false);
  };

  const handleFacultySelect = (fac) => {
    setFaculty(fac.faculty_name || "");
    setFacultyCode(fac.faculty_code || "");
    setShowFacultyViewer(false);
  };

  const resolveFacultyNames = (row) => {
    return [row.faculty_code, row.faculty_code_1, row.faculty_code_2, row.faculty_code_3, row.faculty_code_4]
      .filter(Boolean)
      .map(c => facultyMap[String(c)] || String(c))
      .join(', ');
  };

  const handleView = async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      const params = {};
      if (batchType) params.batch_type = batchType;
      if (branchName) params.branch = branchName;
      if (fromDate) params.from_date = fromDate.toLocaleDateString('en-CA');
      if (toDate) params.to_date = toDate.toLocaleDateString('en-CA');
      if (batchSrNo) params.batch_sr_no = batchSrNo;
      if (facultyCode) params.faculty_code = facultyCode;

      const data = await batchStatusAPI.getManDays(params);
      setTableData(Array.isArray(data) ? data : []);
      setShowTable(true);
    } catch {
      setErrorMsg("Failed to fetch data.");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setBatchType("");
    setBranchName("");
    setFromDate(null);
    setToDate(null);
    setBatchSrNo("");
    setFaculty("");
    setFacultyCode("");
    setTableData([]);
    setShowTable(false);
    setErrorMsg("");
  };

  const fmtDate = (d) => { if (!d) return ''; const [y,m,dd] = d.split('-'); return `${dd}/${m}/${y}`; };

  // Build rowspan map for Faculty Man-Hrs column — merge consecutive rows with same batch_id + faculty_man_hours
  const buildFacultySpanMap = (data) => {
    const spanMap = {};
    let i = 0;
    while (i < data.length) {
      const key = String(data[i].batch_id ?? i);
      const val = data[i].faculty_man_hours ?? '';
      let j = i + 1;
      while (j < data.length && String(data[j].batch_id ?? j) === key && (data[j].faculty_man_hours ?? '') === val) j++;
      spanMap[i] = j - i;
      for (let k = i + 1; k < j; k++) spanMap[k] = 0;
      i = j;
    }
    return spanMap;
  };

  const renderManDaysTable = () => {
    const spanMap = buildFacultySpanMap(tableData);
    const evenBg = '#f9f9f9';

    // TOTAL row — faculty_man_hours counted only once per batch_id
    const seenBatches = new Set();
    let totalFacultyManHrs = 0;
    tableData.forEach(row => {
      const key = String(row.batch_id ?? '');
      if (!seenBatches.has(key)) {
        seenBatches.add(key);
        totalFacultyManHrs += Number(row.faculty_man_hours ?? 0);
      }
    });
    const sum = (field) => tableData.reduce((a, r) => a + Number(r[field] ?? 0), 0);
    const totals = {
      batch_size: sum('batch_size'),
      no_of_participants: sum('no_of_participants'),
      training_man_hours: sum('training_man_hours'),
      training_man_days: sum('training_man_days'),
      no_of_faculty: sum('no_of_faculty'),
      faculty_man_hours: totalFacultyManHrs,
    };

    const headers = [
      "Sr.No.", "Branch Name", "Franchise Name", "Functional Area Name", "Training Course Name",
      "Batch Size", "No of Participants", "Training Start Time", "Training End Time",
      "Training Man Hours", "Training Duration", "Training ManDays",
      "No of Faculty", "Faculty ManHours",
      "Training Start Date", "Training End Date", "Batch Sr No.", "Faculty Name(s)"
    ];

    return (
      <div className="dataTableContainer">
        <table className="dataTable">
          <thead>
            <tr>{headers.map((h, i) => <th key={i}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {tableData.map((row, idx) => {
              const span = spanMap[idx];
              const rowBg = idx % 2 !== 0 ? evenBg : undefined;
              const tdStyle = rowBg ? { backgroundColor: rowBg } : {};
              return (
                <tr key={idx}>
                  <td style={tdStyle}>{idx + 1}</td>
                  <td style={tdStyle}>{row.branch_name ?? ''}</td>
                  <td style={{ ...tdStyle, textAlign: 'left' }}>{row.franchisee_name ?? ''}</td>
                  <td style={tdStyle}>{row.func_area_name ?? ''}</td>
                  <td style={tdStyle}>{row.trng_course_name ?? ''}</td>
                  <td style={tdStyle}>{row.batch_size ?? ''}</td>
                  <td style={tdStyle}>{row.no_of_participants ?? ''}</td>
                  <td style={tdStyle}>{row.start_time ?? ''}</td>
                  <td style={tdStyle}>{row.end_time ?? ''}</td>
                  <td style={tdStyle}>{row.training_man_hours ?? ''}</td>
                  <td style={tdStyle}>{row.training_duration ?? ''}</td>
                  <td style={tdStyle}>{row.training_man_days ?? ''}</td>
                  <td style={tdStyle}>{row.no_of_faculty ?? ''}</td>
                  {span > 0
                    ? <td rowSpan={span} style={{ textAlign: 'center', verticalAlign: 'middle', backgroundColor: rowBg }}>{row.faculty_man_hours ?? ''}</td>
                    : null
                  }
                  <td style={tdStyle}>{fmtDate(row.start_date)}</td>
                  <td style={tdStyle}>{fmtDate(row.end_date)}</td>
                  <td style={tdStyle}>{row.batch_id ?? ''}</td>
                  <td style={tdStyle}>{resolveFacultyNames(row)}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{ backgroundColor: '#6d6d6d', color: '#fff', fontWeight: 600 }}>
              <td colSpan={5} style={{ textAlign: 'center', color: '#fff' }}>TOTAL</td>
              <td style={{ textAlign: 'center', color: '#fff' }}>{totals.batch_size}</td>
              <td style={{ textAlign: 'center', color: '#fff' }}>{totals.no_of_participants}</td>
              <td style={{ color: '#fff' }} /><td style={{ color: '#fff' }} />
              <td style={{ textAlign: 'center', color: '#fff' }}>{totals.training_man_hours}</td>
              <td style={{ color: '#fff' }} />
              <td style={{ textAlign: 'center', color: '#fff' }}>{Number.isInteger(totals.training_man_days) ? totals.training_man_days : totals.training_man_days.toFixed(2)}</td>
              <td style={{ textAlign: 'center', color: '#fff' }}>{totals.no_of_faculty}</td>
              <td style={{ textAlign: 'center', color: '#fff' }}>{totals.faculty_man_hours}</td>
              <td style={{ color: '#fff' }} /><td style={{ color: '#fff' }} /><td style={{ color: '#fff' }} /><td style={{ color: '#fff' }} />
            </tr>
          </tfoot>
        </table>
      </div>
    );
  };

  const handleExportExcel = () => {
    const today = new Date().toLocaleDateString("en-GB");
    const seenBatchesXl = new Set();
    let totalFacultyManHrsXl = 0;
    tableData.forEach(row => {
      const key = String(row.batch_id ?? '');
      if (!seenBatchesXl.has(key)) {
        seenBatchesXl.add(key);
        totalFacultyManHrsXl += Number(row.faculty_man_hours ?? 0);
      }
    });
    const sumXl = (field) => tableData.reduce((a, r) => a + Number(r[field] ?? 0), 0);

    const headers = [
      "Sr.No.", "Branch Name", "Franchise Name", "Functional Area Name", "Training Course Name",
      "Batch Size", "No of Participants", "Training Start Time", "Training End Time",
      "Training Man Hours", "Training Duration", "Training ManDays",
      "No of Faculty", "Faculty ManHours",
      "Training Start Date", "Training End Date", "Batch Sr No.", "Faculty Name(s)"
    ];

    const DATA_OFFSET = 4; // rows 0=title,1=date,2=blank,3=headers → data starts at row 4
    const FACULTY_COL = 13;

    // Track first row per batch for faculty_man_hours
    const seenBatchesXl2 = new Set();
    const data = tableData.map((row, idx) => {
      const key = String(row.batch_id ?? idx);
      const isFirstOfBatch = !seenBatchesXl2.has(key);
      if (isFirstOfBatch) seenBatchesXl2.add(key);
      return [
        idx + 1,
        row.branch_name ?? "",
        row.franchisee_name ?? "",
        row.func_area_name ?? "",
        row.trng_course_name ?? "",
        row.batch_size ?? "",
        row.no_of_participants ?? "",
        row.start_time ?? "",
        row.end_time ?? "",
        row.training_man_hours ?? "",
        row.training_duration ?? "",
        row.training_man_days ?? "",
        row.no_of_faculty ?? "",
        isFirstOfBatch ? (row.faculty_man_hours ?? "") : "",
        row.start_date ?? "",
        row.end_date ?? "",
        row.batch_id ?? "",
        resolveFacultyNames(row),
      ];
    });

    const totalRow = [
      "TOTAL", "", "", "", "",
      sumXl('batch_size'), sumXl('no_of_participants'),
      "", "",
      sumXl('training_man_hours'), "", (() => { const v = sumXl('training_man_days'); return Number.isInteger(v) ? v : Math.round(v * 100) / 100; })(),
      sumXl('no_of_faculty'), totalFacultyManHrsXl,
      "", "", "", ""
    ];

    const ws = XLSX.utils.aoa_to_sheet([
      ["Report Title : Training wise Manhour Calculations"],
      [`Report Date : ${today}`],
      [],
      headers,
      ...data,
      totalRow,
    ]);

    // Merge Faculty ManHours cells (col 13) for consecutive rows sharing same batch_id + faculty_man_hours
    const merges = [];
    let i = 0;
    while (i < tableData.length) {
      const key = String(tableData[i].batch_id ?? i);
      const val = tableData[i].faculty_man_hours ?? '';
      let j = i + 1;
      while (j < tableData.length && String(tableData[j].batch_id ?? j) === key && (tableData[j].faculty_man_hours ?? '') === val) j++;
      if (j > i + 1) {
        merges.push({ s: { r: i + DATA_OFFSET, c: FACULTY_COL }, e: { r: j - 1 + DATA_OFFSET, c: FACULTY_COL } });
      }
      i = j;
    }
    if (merges.length) ws['!merges'] = [...(ws['!merges'] || []), ...merges];

    // Bold the TOTAL row
    const totalRowIdx = data.length + DATA_OFFSET; // after title+date+blank+headers+data rows
    headers.forEach((_, ci) => {
      const cellRef = XLSX.utils.encode_cell({ r: totalRowIdx, c: ci });
      if (!ws[cellRef]) ws[cellRef] = { t: 's', v: '' };
      ws[cellRef].s = { font: { bold: true } };
    });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "TrainingManDays");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array", cellStyles: true });
    saveAs(new Blob([buf], { type: "application/octet-stream" }), "Training_ManDays_Calculation.xlsx");
  };

  return (
    <div className="bd-container">

      {showBatchViewer && (
        <div className="facviewer-overlay">
          <div className="facviewer-modal">
            <button className="facviewer-close" onClick={() => setShowBatchViewer(false)}>×</button>
            <BatchViewer selectable onSelect={handleBatchSelect} autoLoad />
          </div>
        </div>
      )}

      {showFacultyViewer && (
        <div className="facviewer-overlay">
          <div className="facviewer-modal">
            <button className="facviewer-close" onClick={() => setShowFacultyViewer(false)}>×</button>
            <FacultyViewer selectable onSelect={handleFacultySelect} autoLoad />
          </div>
        </div>
      )}

      <section className="filters-panel no-print">
        <div className="filter-single-col">

          <div className="filter-row">
            <label className="filter-label">Batch Type</label>
            <div className="radio-group">
              <label className="radio-item">
                <input type="radio" name="batchType" value="DEFA" checked={batchType === "DEFA"} onChange={() => setBatchType("DEFA")} />
                Godrej Training Batch
              </label>
              <label className="radio-item">
                <input type="radio" name="batchType" value="FREE" checked={batchType === "FREE"} onChange={() => setBatchType("FREE")} />
                Freelance Training Batch
              </label>
            </div>
          </div>

          <div className="filter-row">
            <label className="filter-label">Branch Name</label>
            <select className="filter-input" value={branchName} onChange={(e) => setBranchName(e.target.value)}>
              <option value="">-- Select --</option>
              {branches.map((b) => (
                <option key={b} value={b}>{b}</option>
              ))}
            </select>
          </div>

          <div className="filter-row">
            <label className="filter-label">From Date (DD/MM/YY)</label>
            <div className="tmd-date-wrap" onClick={() => setFromDateOpen(true)}>
              <DatePicker
                open={fromDateOpen}
                onClickOutside={() => setFromDateOpen(false)}
                selected={fromDate}
                onChange={(date) => { setFromDate(date); setFromDateOpen(false); }}
                dateFormat="dd/MM/yyyy"
                className="tmd-date-input"
                placeholderText="Select from date"
                showIcon
                showYearDropdown
                scrollableYearDropdown
                yearDropdownItemNumber={100}
                customInput={<input readOnly />}
              />
            </div>
            <label className="filter-label" style={{ marginLeft: 16 }}>To Date (DD/MM/YY)</label>
            <div className="tmd-date-wrap" onClick={() => setToDateOpen(true)}>
              <DatePicker
                open={toDateOpen}
                onClickOutside={() => setToDateOpen(false)}
                selected={toDate}
                onChange={(date) => { setToDate(date); setToDateOpen(false); }}
                dateFormat="dd/MM/yyyy"
                className="tmd-date-input"
                placeholderText="Select to date"
                minDate={fromDate}
                showIcon
                showYearDropdown
                scrollableYearDropdown
                yearDropdownItemNumber={100}
                customInput={<input readOnly />}
              />
            </div>
          </div>

          <div className="filter-row">
            <label className="filter-label">Batch Sr.No.</label>
            <input className="filter-input" type="text" value={batchSrNo} onChange={(e) => setBatchSrNo(e.target.value)} />
            <button className="btn btn-outline" type="button" onClick={() => setShowBatchViewer(true)}>Get</button>
            <button className="btn btn-secondary" type="button" onClick={() => setBatchSrNo("")}>Clear</button>
          </div>

          <div className="filter-row">
            <label className="filter-label">Faculty</label>
            <input className="filter-input" type="text" value={faculty} readOnly onFocus={(e) => e.target.blur()} />
            <button className="btn btn-outline" type="button" onClick={() => setShowFacultyViewer(true)}>Get</button>
            <button className="btn btn-secondary" type="button" onClick={() => { setFaculty(""); setFacultyCode(""); }}>Clear</button>
          </div>

        </div>

        <div className="action-row">
          <button className="btn btn-outline" onClick={handleReset}>Reset</button>
          <button className="btn btn-outline" onClick={handleView} disabled={loading}>
            {loading ? "Loading…" : "View"}
          </button>
        </div>
      </section>

      {errorMsg && (
        <p className="bd-error">{errorMsg}</p>
      )}

      {showTable && (
        <div className="bd-details tmd-table-scroll">
          {renderManDaysTable()}
          {tableData.length > 0 && (
            <div style={{ display: "flex", justifyContent: "center", margin: "12px 0" }}>
              <button className="btn btn-outline" onClick={handleExportExcel}>Excel Report</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default TrainingManDaysCalculation;
